import { Component, createElement } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formSubmitted } from 'client/actions/form';
import { ValidationError } from 'client/utils/validation-error';

function getFormData(formId, state) {
  const { forms = {} } = state;
  return forms[formId];
}

function getFormContext(formId, page, store, props) {
  return {
    page,
    props: {
      ...props,
      formData: getFormData(formId, store.getState()),
    },
  };
}

/**
 * Converts a Page component into a POST form.
 * A POST form is a special page component that can include a form that will submit back to the server side using POST.
 * The given onSubmit function will be run on the server side, and is responsible for processing the form inputs sent as
 * postData. After processing, any results can be added to redux state where they can be accessed by the page.
 *
 * The state of the form is passed into the page component (or optionally a separate confirmationPage after submission)
 * as a formData prop. The formData prop is an object with the following structure:
 * {
 *   fields {Object} map of field names to values
 *   isSubmitted {boolean} flag denoting that the form was submitted
 *   isValid {boolean} flag denoting whether the form was valid
 *   error {Object} optional error containing a message and fieldErrors map(field name => message)
 * }
 *
 * The onSubmit routine can optionally perform server side validation by throwing a ValidationError.
 * If a ValidationError is thrown, the formData will be sent back to the page component with isValid set to false,
 * along with an error attribute containing the message and fieldErrors map.
 *
 * @param {Component} page A page component
 * @param {Object} options An object of the following format:
 *  {
 *    onSubmit: An async function(postData, store, props) to be run on the server side when the form is submitted,
 *    confirmationPage: Optional page component to render after a successful submission,
 *    formId: Optional explicit formId to use, pageName will be used by default
 *  }
 *
 * @return A react component that renders the page.
 */
export function postForm(page, options = {}) {
  const { formId = page.getName(), confirmationPage = page, onSubmit } = options;

  class FormWrapper extends Component {
    static isForm = true;

    static formId = formId;

    static onSubmit(postData, store, props) {
      if (typeof onSubmit === 'function') {
        return onSubmit(postData, store, props)
          .then(() => {
            store.dispatch(formSubmitted(formId, postData, true));
            return getFormContext(formId, confirmationPage, store, props);
          })
          .catch(error => {
            if (error instanceof ValidationError) {
              store.dispatch(formSubmitted(formId, postData, false, error));
              return getFormContext(formId, page, store, props);
            }

            throw error;
          });
      }

      store.dispatch(formSubmitted(formId, postData, true));
      return Promise.resolve(getFormContext(formId, confirmationPage, store, props));
    }

    static getPage() {
      return page;
    }

    static getConfirmationPage() {
      return confirmationPage;
    }

    static propTypes = {
      formData: PropTypes.shape({
        isSubmitted: PropTypes.bool,
        isValid: PropTypes.bool,
      }),
    };

    static defaultProps = {
      formData: {},
    };

    render() {
      const { formData } = this.props;

      if (formData.isSubmitted && formData.isValid) {
        return createElement(confirmationPage, this.props);
      }

      return createElement(page, this.props);
    }
  }

  const mapStateToProps = state => ({
    formData: getFormData(formId, state),
  });

  return connect(mapStateToProps)(FormWrapper);
}
